import { CSSProperties, useLayoutEffect, useRef } from 'react'

import { usePrefersReducedMotion } from '@sequencehq/utils'

const WAVE_THRESH = 3
const CHAR_MULT = 3
const ANIM_STEP = 40
const WAVE_BUF = 5
const CHAR_REVEAL_STAGGER = 60 // ms between each character fade-in

const DEFAULT_CHARS = '.-?!@£$%^&*abcdefghijklmnopqrstuvwxyz0123456789'

export interface AsciiShiftOptions {
  dur?: number
  chars?: string
  preserveSpaces?: boolean
  spread?: number
  revealDelay?: number
  active?: boolean
}

interface Wave {
  startPos: number
  startTime: number
  /** Furthest character distance from `startPos`; precomputed once per wave. */
  maxDist: number
}

/**
 * Animated "ASCII scramble" text effect ported from sequence-marketing's labs
 * header. Two behaviours: a cursor-following scramble wave on hover, and an
 * optional reveal-on-mount (pass `revealDelay`) that fades characters in one at
 * a time before a settling wave.
 *
 * The effect imperatively rewrites the element's text content (and, in reveal
 * mode, builds per-character child spans) on each animation frame, so the
 * returned ref must be attached to a leaf text element.
 */
export const useAsciiShift = (text: string, opts: AsciiShiftOptions = {}) => {
  const {
    dur = 600,
    chars = DEFAULT_CHARS,
    preserveSpaces = true,
    spread = 0.3,
    revealDelay,
    active = true
  } = opts

  const elRef = useRef<HTMLSpanElement>(null)
  const hasRevealedRef = useRef(false)
  const prefersReducedMotion = usePrefersReducedMotion()

  useLayoutEffect(() => {
    const el = elRef.current
    if (!el) return

    // Respect the user's reduced-motion preference: show the final text and skip
    // all scramble/reveal animation (as the sibling labs effects do).
    if (prefersReducedMotion) {
      el.textContent = text
      return
    }

    const origChars = text.split('')
    let isAnim = false
    let cursorPos = 0
    let waves: Wave[] = []
    let animId: number | null = null
    let isHover = false
    let origW: number | null = null
    let charSpans: HTMLSpanElement[] | null = null

    // Reveal-on-mount plays at most once per mount, and only while `active`. When
    // we're going to reveal, build per-character spans that start invisible so each
    // can be faded in independently (created imperatively because the effect
    // rewrites their content every animation frame). Otherwise — already revealed,
    // inactive, or no `revealDelay` — render the final text directly so it's never
    // stranded at opacity 0 (e.g. when the effect re-runs after the reveal, such as
    // on a reduced-motion toggle).
    const willReveal =
      revealDelay !== undefined && active && !hasRevealedRef.current
    if (willReveal) {
      el.textContent = ''
      charSpans = origChars.map(char => {
        const span = document.createElement('span')
        span.textContent = char
        span.style.opacity = '0'
        span.style.transition = 'opacity 50ms linear 0ms'
        span.style.display = 'inline-block'
        if (char === ' ') span.style.width = '0.25em'
        el.appendChild(span)
        return span
      })
    } else {
      el.textContent = text
    }

    const setText = (str: string) => {
      if (charSpans) {
        const next = str.split('')
        charSpans.forEach((span, i) => {
          span.textContent = next[i] ?? ''
        })
      } else {
        el.textContent = str
      }
    }

    const updateCursorPos = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      const x = e.clientX - rect.left
      const len = origChars.length
      const pos = Math.round((x / rect.width) * len)
      cursorPos = Math.max(0, Math.min(pos, len - 1))
    }

    // Pin the element's width to its rendered width so scrambled glyphs (which
    // may be wider/narrower than the originals) don't make the layout jitter.
    const lockWidth = () => {
      if (origW === null) {
        origW = el.getBoundingClientRect().width
        el.style.width = `${origW}px`
      }
    }

    const stop = () => {
      setText(text)
      if (origW !== null) {
        el.style.width = ''
        origW = null
      }
      isAnim = false
    }

    const start = () => {
      if (isAnim) return
      lockWidth()
      isAnim = true

      const animate = () => {
        const t = Date.now()
        waves = waves.filter(w => t - w.startTime < dur)

        if (waves.length === 0) {
          stop()
          return
        }

        const result = origChars
          .map((char, i) => {
            if (preserveSpaces && char === ' ') return ' '

            // Each wave near its leading edge scrambles this character (later
            // waves win); characters a wave hasn't reached keep their glyph.
            return waves.reduce((acc, w) => {
              const age = t - w.startTime
              const prog = Math.min(age / dur, 1)
              const dist = Math.abs(i - w.startPos)
              const rad = (prog * (w.maxDist + WAVE_BUF)) / spread

              if (dist > rad) return acc

              const intens = Math.max(0, rad - dist)
              const isOnWaveEdge = intens <= WAVE_THRESH && intens > 0
              const ci =
                (dist * CHAR_MULT + Math.floor(age / ANIM_STEP)) % chars.length

              return isOnWaveEdge ? chars[ci] : acc
            }, char)
          })
          .join('')

        setText(result)
        animId = requestAnimationFrame(animate)
      }

      animId = requestAnimationFrame(animate)
    }

    const startWave = () => {
      waves.push({
        startPos: cursorPos,
        startTime: Date.now(),
        maxDist: Math.max(cursorPos, origChars.length - cursorPos - 1)
      })
      if (!isAnim) start()
    }

    const handleEnter = (e: MouseEvent) => {
      isHover = true
      updateCursorPos(e)
      startWave()
    }

    const handleMove = (e: MouseEvent) => {
      if (!isHover) return
      const old = cursorPos
      updateCursorPos(e)
      if (cursorPos !== old) startWave()
    }

    const handleLeave = () => {
      isHover = false
    }

    el.addEventListener('mouseenter', handleEnter)
    el.addEventListener('mousemove', handleMove)
    el.addEventListener('mouseleave', handleLeave)

    // Reveal-on-mount: after `revealDelay`, fade each character in on a stagger
    // and fire a settling scramble wave. `willReveal` already gated this to the
    // first active mount, so it runs at most once.
    let revealTimeout: ReturnType<typeof setTimeout> | null = null
    const revealCharTimeouts: ReturnType<typeof setTimeout>[] = []

    if (willReveal) {
      revealTimeout = setTimeout(() => {
        hasRevealedRef.current = true
        lockWidth()
        cursorPos = 0
        startWave()

        charSpans?.forEach((span, i) => {
          const timeout = setTimeout(() => {
            span.style.opacity = '1'
          }, i * CHAR_REVEAL_STAGGER)
          revealCharTimeouts.push(timeout)
        })
      }, revealDelay)
    }

    return () => {
      if (animId !== null) cancelAnimationFrame(animId)
      if (revealTimeout) clearTimeout(revealTimeout)
      revealCharTimeouts.forEach(clearTimeout)
      el.removeEventListener('mouseenter', handleEnter)
      el.removeEventListener('mousemove', handleMove)
      el.removeEventListener('mouseleave', handleLeave)
      waves = []
      // NB: don't null `origW` here — stop() releases the width-lock only when
      // `origW !== null`, and nulls it itself afterwards. `charSpans = null`
      // before stop() is required so its final setText() writes to `el` directly.
      charSpans = null
      stop()
    }
  }, [
    text,
    dur,
    chars,
    preserveSpaces,
    spread,
    revealDelay,
    active,
    prefersReducedMotion
  ])

  return elRef
}

type AsciiShiftProps = {
  text: string
  style?: CSSProperties
} & AsciiShiftOptions

/**
 * Renders `text` with the {@link useAsciiShift} scramble effect. The `style`
 * object (typography tokens, color, etc.) is applied to the span so the caller
 * controls the appearance.
 */
export const AsciiShift = ({
  text,
  dur,
  chars,
  preserveSpaces,
  spread,
  revealDelay,
  active,
  style
}: AsciiShiftProps) => {
  // The hook keys its effect on the individual option values, so a fresh object
  // each render is fine — no need to memoise it.
  const ref = useAsciiShift(text, {
    dur,
    chars,
    preserveSpaces,
    spread,
    revealDelay,
    active
  })

  return (
    // inline-block makes the effect's width-lock take effect (inline elements
    // ignore width); nowrap stops scrambled glyphs reflowing onto a new line in
    // proportional fonts. Both are overridable via style.
    <span
      ref={ref}
      style={{ display: 'inline-block', whiteSpace: 'nowrap', ...style }}
    >
      {text}
    </span>
  )
}
